"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useLocale, useTranslations } from "next-intl";

import { Feature } from "@/components/ui/Feature";
import { getRedirectUrl } from "@/data/helper/getRedirectUrl";
import { FeatureFlags } from "@/enums/feature-flags";
import { EnumViewMode } from "@/enums/view-mode";
import { useUserRole } from "@/hooks/useUserRole";
import { useRouter } from "@/i18n/routing";
import IconCard from "@/icons/view_card.svg";
import IconGrid from "@/icons/view_grid.svg";
import IconList from "@/icons/view_list.svg";
import IconTable from "@/icons/view_table.svg";
import { useViewMode } from "@/providers/ViewContext";
import { SearchResultHeaderProps } from "@/types/search.types";
import { Filter } from "@/types/SearchFilter.types";
import { cn } from "@/utils/cn";
import { getGroupedSortOptions } from "@/utils/groupedSortOptions";
import { initialFilterValue } from "@/utils/initialFilterValue";
import { serializeFiltersToQuery } from "@/utils/serializeFiltersToQuery";
import { mergeUnique, objectFromSearchParams } from "@/utils/urlHelpers";

import { GroupedSortSelect } from "./GroupedSortSelect";
import { SortSelect } from "./SortSelect";
import styles from "./style.module.scss";

export const SORT_KEY = "avezor_sort_preference";
export const DEFAULT_SORT_VALUE = "date_desc";

export const SearchResultHeader = ({
  total,
  className,
  defaultSort = DEFAULT_SORT_VALUE,
  dealType,
  pageSize,
  onPageSizeChange,
  useGroupedSort = false,
  isCatalogPage
}: SearchResultHeaderProps) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname()
  const searchParams = useSearchParams();
  const { viewMode, handleChangeViewMode } = useViewMode();
  const { isRealtor } = useUserRole();
  const locale = useLocale()

  const [currentSort, setCurrentSort] = useState(defaultSort);

  const sortOptions = useMemo(
    () => [
      { value: "price_asc", label: t("Price: low to high") },
      { value: "price_desc", label: t("Price: high to low") },
      { value: "date_desc", label: t("Newest first") },
      { value: "date_asc", label: t("Oldest first") },
    ],
    [t],
  );

  const groupedSortOptions = useMemo(() => getGroupedSortOptions(t), [t]);

  const handleSortChange = useCallback(
    (value: string) => {
      setCurrentSort(value);
      localStorage.setItem(SORT_KEY, value);

      const currentParams = objectFromSearchParams(searchParams);

      const filters: Partial<Filter & { sort: string }> = {
        ...initialFilterValue(dealType),
        ...currentParams,
      };

      if (value === DEFAULT_SORT_VALUE) {
        delete filters.dealType;
        delete filters.limit;
        delete filters.page;
        delete filters.sort;
      } else if (value !== DEFAULT_SORT_VALUE) {
        filters.sort = value;
      }
      if (isCatalogPage) {
        const cleanPath = pathname.replace(new RegExp(`^/${locale}`), "");
        const [, objectType, city, district] = cleanPath
          .split("/")
          .filter(Boolean);


        if (objectType) filters.objectType = mergeUnique(filters.objectType, objectType);
        if (city) filters.city = mergeUnique(filters.city, city);
        if (district) filters.district = mergeUnique(filters.district, district);

      }

      const serializedQuery = serializeFiltersToQuery(filters);

      // ФИКС: Убираем page при изменении сортировки для сброса на первую страницу
      delete serializedQuery.page;

      router.push({
        pathname: isCatalogPage ? pathname : getRedirectUrl(dealType),
        query: serializedQuery,
      }, {
        scroll: false,
      });
    },
    [searchParams, dealType, isCatalogPage, router, pathname, locale],
  );

  useEffect(() => {
    const savedSort =
      (searchParams && searchParams.get("sort")) ||
      localStorage.getItem(SORT_KEY);
    if (savedSort) {
      setCurrentSort(savedSort);
    }
  }, [searchParams]);

  return (
    <div className={cn(styles.wrapper, className)}>
      <div
        className={
          "flex justify-between flex-wrap items-center w-full h-auto md:h-[5.4rem] gap-[1rem] xl:gap-[2rem]"
        }
      >
        <div className={cn(styles.viewButtons, "lg:order-1")}>
          <button
            className={cn(
              styles.viewButton,
              "hover:text-red text-gray-1200 border border-solid border-gray-100",
              viewMode === EnumViewMode.GRID && "text-red border-red",
            )}
            onClick={() => handleChangeViewMode(EnumViewMode.GRID)}
            title={t("Grid view")}
          >
            <IconGrid className={styles.icon} />
          </button>
          <button
            className={cn(
              styles.viewButton,
              "hover:text-red text-gray-1200 border border-solid border-gray-100",
              viewMode === EnumViewMode.CARD && "text-red border-red",
            )}
            onClick={() => handleChangeViewMode(EnumViewMode.CARD)}
            title={t("Card view")}
          >
            <IconCard className={styles.icon} />
          </button>
          <button
            className={cn(
              styles.viewButton,
              "hover:text-red text-gray-1200 border border-solid border-gray-100",
              viewMode === EnumViewMode.LIST && "text-red border-red",
            )}
            onClick={() => handleChangeViewMode(EnumViewMode.LIST)}
            title={t("List view")}
          >
            <IconList className={styles.icon} />
          </button>
          <Feature flag={FeatureFlags.PROPERTIES_LIST_TABLE_VIEW}>
            <Feature.Enabled>
              {isRealtor && (
                <button
                  className={cn(
                    styles.viewButton,
                    "hover:text-red text-gray-1200 border border-solid border-gray-100",
                    viewMode === EnumViewMode.TABLE && "text-red border-red",
                  )}
                  onClick={() => handleChangeViewMode(EnumViewMode.TABLE)}
                  title={t("Table view")}
                >
                  <IconTable className={styles.icon} />
                </button>
              )}
            </Feature.Enabled>
          </Feature>
        </div>
        <span
          className={cn(styles.resultText, "lg:order-2")}
          suppressHydrationWarning
        >
          <span className={"hidden md:inline"}>{t("Found")}:</span> &nbsp;
          {t("pluralization.property", { count: total || 0 })}
        </span>
        <div className={"grow hidden lg:block order-3"}></div>
        <div className="flex items-center gap-[1rem] order-1 lg:order-4">
          {useGroupedSort ? (
            <GroupedSortSelect
              value={currentSort}
              groupedOptions={groupedSortOptions}
              onChange={handleSortChange}
              className={cn(styles.sortSelect)}
            />
          ) : (
            <SortSelect
              value={currentSort}
              options={sortOptions}
              onChange={handleSortChange}
              className={cn(styles.sortSelect)}
            />
          )}
          {/* {typeof pageSize === "number" &&
            typeof onPageSizeChange === "function" && (
              <PageSizeSelect
                value={pageSize}
                onChange={onPageSizeChange}
                className={cn(styles.sortSelect)}
              />
            )} */}
        </div>
        {/* TODO: Uncomment for map view */}
        {/* <button
          className={cn(styles.button, { [styles.active]: viewMode === EnumViewMode.MAP })}
          onClick={() => handleChangeViewMode(EnumViewMode.MAP)}
          title={t("View on map")}
        >
          {t("View on map")}
          <IconMap className={styles.iconStyle}  />
        </button> */}
      </div>
    </div>
  );
};
